import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

export interface ExerciseData {
  name: string;
  sets: { reps: number; weight: number }[];
  completed: boolean;
  skipped?: boolean;
}

export interface WorkoutSessionRecord {
  id: string;
  completed_at: string;
  duration_minutes?: number;
  exercises_completed?: number;
  notes?: string;
  exercise_data?: ExerciseData[];
}

export function useWorkoutSessions(userId: string | undefined) {
  const [sessions, setSessions] = useState<WorkoutSessionRecord[]>([]);
  const [totalWorkouts, setTotalWorkouts] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error, count } = await supabase
        .from('workout_sessions')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .order('completed_at', { ascending: false })
        .limit(30);

      if (error) throw error;

      // Map database results to our interface
      const mappedSessions: WorkoutSessionRecord[] = (data || []).map(row => ({
        id: row.id,
        completed_at: row.completed_at,
        duration_minutes: row.duration_minutes ?? undefined,
        exercises_completed: row.exercises_completed ?? undefined,
        notes: row.notes ?? undefined,
        exercise_data: (row.exercise_data as unknown as ExerciseData[] | null) ?? undefined,
      }));

      setSessions(mappedSessions);
      setTotalWorkouts(count || 0);
    } catch (error) {
      console.error('Error fetching workout sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const logWorkout = useCallback(async (
    exercisesCompleted?: number,
    notes?: string,
    exerciseData?: ExerciseData[]
  ) => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('workout_sessions')
        .insert([{
          user_id: userId,
          exercises_completed: exercisesCompleted,
          notes,
          exercise_data: JSON.parse(JSON.stringify(exerciseData || [])) as Json,
        }])
        .select()
        .single();

      if (error) throw error;

      const mappedSession: WorkoutSessionRecord = {
        id: data.id,
        completed_at: data.completed_at,
        duration_minutes: data.duration_minutes ?? undefined,
        exercises_completed: data.exercises_completed ?? undefined,
        notes: data.notes ?? undefined,
        exercise_data: (data.exercise_data as unknown as ExerciseData[] | null) ?? undefined,
      };

      setSessions(prev => [mappedSession, ...prev]);
      setTotalWorkouts(prev => prev + 1);
      return mappedSession;
    } catch (error) {
      console.error('Error logging workout:', error);
      return null;
    }
  }, [userId]);

  return {
    sessions,
    totalWorkouts,
    loading,
    logWorkout,
    refetch: fetchSessions,
  };
}